// FieldGuideWire — a subtle teal "wire" that points from the cursor to the
// nearest empty field on the active step, to guide the eye on a long form.
//
// Self-contained: no deps, plain React + inline SVG/CSS. Mounted once inside
// StepLayout's wrapper so it auto-scopes to the live `.step-workbench`.
//
// Required-ness lives in each step's Zod schema, NOT in any DOM marker, so we
// target ANY visible/enabled/empty real input control in `.step-workbench`
// (skipping hidden/file/checkbox/radio and the `.prefill-card` autofill helper).
//
// Behavior (tasteful / show-only-when-useful):
//   - The anchor sits INSIDE the field, ~22px in on the empty left zone, at the
//     field's vertical center (never on the border, never over placeholder text).
//   - The whole overlay is shown only at "where do I go next?" moments: when the
//     cursor goes idle (~500ms still) or right after a field is filled. It fades
//     out (150ms) while a control is focused, while the user is typing, or while
//     the cursor is actively moving — then re-shows on idle.
//   - The node snaps (200ms ease-out) to the next nearest empty field as fields
//     get filled. Renders nothing when no empty field remains (step done) or
//     under prefers-reduced-motion. pointer-events:none, fixed full-viewport SVG.
import { useEffect, useRef, useState } from 'react'

const TEAL = '#45BDA6'
const IDLE_MS = 500 // cursor-still delay before the wire re-appears
const TYPING_MS = 600 // how long after an input event we treat the user as typing

const CSS = `
@keyframes fgwFlow { to { stroke-dashoffset: -24; } }
@keyframes fgwRing { 0% { transform: scale(1); opacity: .4; } 100% { transform: scale(1.4); opacity: 0; } }
.fgw-wire { stroke-dasharray: 5 7; animation: fgwFlow 1.1s linear infinite; }
.fgw-ring { transform-box: fill-box; transform-origin: center; animation: fgwRing 2.4s ease-out infinite; }
`

// Input types that count as a fillable text-ish control. type=hidden/file/
// checkbox/radio/submit/button/etc. are skipped.
const TEXTY_INPUT_TYPES = new Set(['text', 'email', 'tel', 'url', 'number', 'search', ''])

/** Is this real control currently empty? */
function isEmptyControl(el) {
  if (el.tagName === 'SELECT') return el.value === '' || el.selectedIndex <= 0
  if (el.tagName === 'TEXTAREA') return String(el.value ?? '').trim() === ''
  // INPUT
  const type = (el.getAttribute('type') || '').toLowerCase()
  if (!TEXTY_INPUT_TYPES.has(type)) return false
  return String(el.value ?? '').trim() === ''
}

/** Is this element a real fillable form control? (for focus/input filtering) */
function isFormControl(el) {
  return el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')
}

/** Nearest visible + enabled + empty control to (cx, cy). Returns anchor or null. */
function findTarget(cx, cy) {
  const wb = document.querySelector('.step-workbench')
  if (!wb) return null
  let best = null
  let bestDist = Infinity
  // Anchor ~22px inside the element's left edge (never past 40% of its width),
  // vertically centered — same as the field anchor.
  const consider = (el) => {
    const r = el.getBoundingClientRect()
    if (r.width === 0 && r.height === 0) return
    const ccx = r.left + r.width / 2
    const ccy = r.top + r.height / 2
    const d = (ccx - cx) ** 2 + (ccy - cy) ** 2
    if (d < bestDist) {
      bestDist = d
      const inset = Math.min(22, r.width * 0.4)
      best = { x: Math.round(r.left + inset), y: Math.round(r.top + r.height / 2) }
    }
  }
  // 1) Empty text-ish form controls.
  for (const el of wb.querySelectorAll('input, select, textarea')) {
    if (el.disabled || el.readOnly) continue
    if (el.offsetParent === null) continue // hidden (display:none / collapsed)
    if (el.closest('.prefill-card')) continue // website "Find my practice" helper
    if (el.tagName === 'INPUT' && !TEXTY_INPUT_TYPES.has((el.getAttribute('type') || '').toLowerCase())) continue
    if (!isEmptyControl(el)) continue
    consider(el)
  }
  // 2) Explicit unfilled targets — e.g. a required chip group (ship-to) with
  //    nothing selected. The component sets data-fgw-target="true" only while
  //    unfilled and removes it once a choice is made.
  for (const el of wb.querySelectorAll('[data-fgw-target="true"]')) {
    if (el.offsetParent === null) continue
    consider(el)
  }
  return best
}

/** Gentle quadratic curve cursor → target with a small perpendicular bow. */
function curvePath(cx, cy, tx, ty) {
  const mx = (cx + tx) / 2
  const my = (cy + ty) / 2
  const dx = tx - cx
  const dy = ty - cy
  const len = Math.hypot(dx, dy) || 1
  const bow = Math.min(60, len * 0.18)
  const px = mx + (-dy / len) * bow
  const py = my + (dx / len) * bow
  return `M ${cx} ${cy} Q ${px.toFixed(1)} ${py.toFixed(1)} ${tx} ${ty}`
}

export default function FieldGuideWire() {
  // wire = { cx, cy, tx, ty } | null — geometry (kept while a target exists)
  const [wire, setWire] = useState(null)
  // show — whether the overlay is faded in right now (drives opacity transition)
  const [show, setShow] = useState(false)

  const cursorRef = useRef({ x: 0, y: 0 })
  const seenMoveRef = useRef(false)
  const focusedRef = useRef(false) // a form control is focused
  const movingRef = useRef(false) // cursor moved within the last IDLE_MS
  const typingRef = useRef(false) // an input event fired within the last TYPING_MS
  const idleTimerRef = useRef(0)
  const typingTimerRef = useRef(0)
  const rafRef = useRef(0)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    cursorRef.current = { x: window.innerWidth / 2, y: window.innerHeight / 2 }

    const recompute = () => {
      const { x, y } = cursorRef.current
      const t = findTarget(x, y)
      // No empty field (step done) or the user hasn't engaged yet → render
      // nothing at all (whole overlay unmounts, line + node together).
      if (!t || !seenMoveRef.current) {
        setWire(null)
        setShow(false)
        return
      }
      setWire({ cx: x, cy: y, tx: t.x, ty: t.y })
      // Visible only at "where next?" moments: cursor idle, not typing, not
      // focused into a control.
      setShow(!focusedRef.current && !movingRef.current && !typingRef.current)
    }

    // rAF-throttle so continuous mousemove/scroll stays smooth.
    const schedule = () => {
      if (rafRef.current) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0
        recompute()
      })
    }

    const onMove = (e) => {
      cursorRef.current = { x: e.clientX, y: e.clientY }
      seenMoveRef.current = true
      movingRef.current = true // actively moving → fade out
      schedule()
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        movingRef.current = false // cursor has gone still → re-show on idle
        recompute()
      }, IDLE_MS)
    }

    const onFocusIn = (e) => {
      if (!isFormControl(e.target)) return
      focusedRef.current = true // focused a control → fade out
      schedule()
    }

    const onFocusOut = (e) => {
      if (!isFormControl(e.target)) return
      focusedRef.current = false // field completed & focus left → re-show next
      schedule()
    }

    const onInput = (e) => {
      if (!isFormControl(e.target)) return
      typingRef.current = true // actively typing / filling → fade out
      schedule()
      clearTimeout(typingTimerRef.current)
      typingTimerRef.current = setTimeout(() => {
        typingRef.current = false // a fill just settled → re-show next target
        recompute()
      }, TYPING_MS)
    }

    const onScroll = () => schedule()

    window.addEventListener('mousemove', onMove, { passive: true })
    window.addEventListener('input', onInput, true)
    window.addEventListener('change', onInput, true)
    window.addEventListener('focusin', onFocusIn, true)
    window.addEventListener('focusout', onFocusOut, true)
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      clearTimeout(idleTimerRef.current)
      clearTimeout(typingTimerRef.current)
      window.removeEventListener('mousemove', onMove, { passive: true })
      window.removeEventListener('input', onInput, true)
      window.removeEventListener('change', onInput, true)
      window.removeEventListener('focusin', onFocusIn, true)
      window.removeEventListener('focusout', onFocusOut, true)
      window.removeEventListener('scroll', onScroll, { passive: true, capture: true })
    }
  }, [])

  if (!wire) return null

  return (
    <>
      <style>{CSS}</style>
      <svg
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 40,
          overflow: 'visible',
          opacity: show ? 1 : 0,
          transition: 'opacity 150ms ease-out',
        }}
      >
        <defs>
          <linearGradient
            id="fgwGrad"
            gradientUnits="userSpaceOnUse"
            x1={wire.cx}
            y1={wire.cy}
            x2={wire.tx}
            y2={wire.ty}
          >
            <stop offset="0" stopColor={TEAL} stopOpacity="0" />
            <stop offset="0.55" stopColor={TEAL} stopOpacity="0.2" />
            <stop offset="1" stopColor={TEAL} stopOpacity="0.45" />
          </linearGradient>
        </defs>

        {/* the wire — soft, thin, with a gentle bow toward the field */}
        <path
          className="fgw-wire"
          d={curvePath(wire.cx, wire.cy, wire.tx, wire.ty)}
          fill="none"
          stroke="url(#fgwGrad)"
          strokeWidth="1.25"
          strokeLinecap="round"
        />

        {/* anchor node — small solid dot + a calm slow-pulsing ring; snaps to
            the next nearest empty field on fill */}
        <g style={{ transform: `translate(${wire.tx}px, ${wire.ty}px)`, transition: 'transform 0.2s ease-out' }}>
          <circle className="fgw-ring" r="5" fill="none" stroke={TEAL} strokeWidth="1" strokeOpacity="0.35" />
          <circle r="3" fill={TEAL} />
        </g>
      </svg>
    </>
  )
}
